import { useState, useEffect } from 'react';
import { API_ORIGIN } from '../config/apiOrigin';

/**
 * Hook to fetch all available years from the API
 */
export function useYearsData() {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchYears = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_ORIGIN}/api/years`, { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to fetch years');
        
        const data = await response.json();
        setYears(data.years);
        setError(null);
      } catch (err) {
        setError(err.message);
        setYears([]);
      } finally {
        setLoading(false);
      }
    };

    fetchYears();
  }, []);

  return { years, loading, error };
}

/**
 * Hook to fetch top tracks for a specific year
 */
export function useYearTopTracks(year, limit = 100) {
  const [tracks, setTracks] = useState([]);
  const [tracksYear, setTracksYear] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!year) {
      setTracks([]);
      setTracksYear(null);
      return;
    }
    let cancelled = false;
    setTracks([]);
    setTracksYear(null);

    const fetchTracks = async () => {
      try {
        setLoading(true);
        const url = `${API_ORIGIN}/api/year/${year}/top-tracks?limit=${limit}`;
        const response = await fetch(url, { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to fetch tracks');
        
        const data = await response.json();
        if (cancelled) return;
        setTracks(data.tracks);
        setTracksYear(Number(data?.year || year));
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
        setTracks([]);
        setTracksYear(null);
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    };

    fetchTracks();
    return () => {
      cancelled = true;
    };
  }, [year, limit]);

  return { tracks, tracksYear, loading, error };
}

/**
 * Hook to fetch top artists for a specific year
 */
export function useYearTopArtists(year, limit = 50) {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!year) return;

    const fetchArtists = async () => {
      try {
        setLoading(true);
        const url = `${API_ORIGIN}/api/year/${year}/top-artists?limit=${limit}`;
        const response = await fetch(url, { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to fetch artists');
        
        const data = await response.json();
        setArtists(data.artists);
        setError(null);
      } catch (err) {
        setError(err.message);
        setArtists([]);
      } finally {
        setLoading(false);
      }
    };

    fetchArtists();
  }, [year, limit]);

  return { artists, loading, error };
}

/**
 * Hook to fetch summary statistics
 */
export function useStatsSummary() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_ORIGIN}/api/stats`, { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to fetch stats');
        
        const data = await response.json();
        setStats(data);
        setError(null);
      } catch (err) {
        setError(err.message);
        setStats(null);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return { stats, loading, error };
}
